import React, { useMemo, useState, useEffect } from "react";
import { formatTimestamp, getMethodLabel, getStatusBadge } from "./constants";
import {
  IScrubSession,
  IScrubRecordsResponse,
} from "src/interfaces/Reports/IScrubSession";
import { callApi, HttpMethod } from "src/services/apiService";
import { runtimeConfig } from "src/services/runtimeConfig";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridPageChangeEvent,
} from "@progress/kendo-react-grid";

// --- Custom Cells ---

const DateCell: React.FC<GridCellProps> = (props) => {
  const { dataItem, field } = props;
  if (!dataItem || !field) return <td>-</td>;
  return <td>{formatTimestamp(dataItem[field])}</td>;
};

const StatusCell: React.FC<GridCellProps> = (props) => {
  const { dataItem } = props;
  const badge = getStatusBadge(dataItem.statusId);
  const label = dataItem.statusDescription || "Unknown";
  return (
    <td>
      <span className={`badge ${badge}`}>{label}</span>
    </td>
  );
};

const MethodCell: React.FC<GridCellProps> = (props) => {
  const { dataItem } = props;
  return <td>{getMethodLabel(dataItem.receivedBy)}</td>;
};

const FileNameCell: React.FC<GridCellProps> = (props) => {
  const { dataItem } = props;
  const method = dataItem.receivedBy?.toUpperCase();

  if (method === "R") {
    return <td className="text-muted fst-italic">-- API Request --</td>;
  }

  const hasOutputFile = (method === "W" || method === "F") && dataItem.multiFileOutputFile;
  if (hasOutputFile) {
    const downloadUrl = `${runtimeConfig.apiUrl}/reports/scrub-sessions/${dataItem.sessionId}/download-output`;
    return (
      <td>
        <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
          <i className="bi bi-download me-1"></i>
          {dataItem.originalFilename}
        </a>
      </td>
    );
  }

  return <td>{dataItem.originalFilename || "-"}</td>;
};

const NumberCell: React.FC<GridCellProps> = (props) => {
  const { dataItem, field } = props;
  if (!dataItem || !field) return <td>-</td>;
  const value = dataItem[field];
  return <td>{value?.toLocaleString() || "0"}</td>;
};

const CampaignCell: React.FC<GridCellProps> = (props) => (
  <td>{props.dataItem?.campaignName || "-"}</td>
);


const IpAddressCell: React.FC<GridCellProps> = (props) => (
  <td>{props.dataItem?.receivedByData1 || "-"}</td>
);

// Format raw phone string (e.g. "5551234567") as "(555) 123-4567"
const formatPhoneNumber = (phone: string | null): string => {
  if (!phone) return "-";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

interface PhoneRecord {
  phoneNumber: string;
  status: string;
}


// --- Props ---

interface ScrubSessionDetailProps {
  session: IScrubSession;
  onClose: () => void;
}

// --- Component ---

const ScrubSessionDetail: React.FC<ScrubSessionDetailProps> = ({
  session,
  onClose,
}) => {
  const isApi = session.receivedBy?.toUpperCase() === "R";

  // Fetch phone records for API sessions with server-side pagination
  const [phoneRecords, setPhoneRecords] = useState<PhoneRecord[]>([]);
  const [phoneRecordsTotal, setPhoneRecordsTotal] = useState(0);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [recordsPage, setRecordsPage] = useState({ skip: 0, take: 25 });

  const data = useMemo(
    () => [{ ...session, phoneRecords }],
    [session, phoneRecords],
  );

  useEffect(() => {
    if (!isApi) return;

    setIsLoadingRecords(true);

    const fetchRecords = async () => {
      try {
        const response: IScrubRecordsResponse = await callApi(
          `reports/scrub-sessions/${session.sessionId}/records?skip=${recordsPage.skip}&take=${recordsPage.take}`,
          HttpMethod.GET,
        );
        const records = response.data
          ?.filter((r) => !!r.phoneNumber)
          .map((r) => ({ phoneNumber: r.phoneNumber!, status: r.status || "" }));
        setPhoneRecords(records || []);
        setPhoneRecordsTotal(response.total || 0);
      } catch (err) {
        console.error("Error fetching scrub records:", err);
      } finally {
        setIsLoadingRecords(false);
      }
    };

    fetchRecords();
  }, [isApi, session.sessionId, recordsPage]);

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="scrub-session-detail-title"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "95vw" }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="scrub-session-detail-title">
              Session Details
              <small className="text-muted ms-2">
                #{session.sessionId}
                {session.receivedBy?.toUpperCase() !== "R" &&
                  session.originalFilename &&
                  ` - ${session.originalFilename}`}
              </small>
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body p-3">
            <Grid data={data} scrollable="none">
              <Column field="sessionId" title="Session ID" />
              <Column field="receivedOn" title="Date" cell={DateCell} />
              <Column field="originalFilename" title="File Name" cell={FileNameCell} />
              <Column field="statusId" title="Status" cell={StatusCell} />
              <Column field="receivedBy" title="Method" cell={MethodCell} />
              <Column field="campaignName" title="Campaign" cell={CampaignCell} />
              <Column field="userId" title="User ID" />
              <Column field="receivedByData1" title="IP Address" cell={IpAddressCell} />
              <Column field="timeLastScrubbedInSecs" title="Scrub Time (s)" cell={NumberCell} />
              <Column field="numOfTotalRecords" title="Total" cell={NumberCell} />
              <Column field="numOfBillableRecords" title="Billable" cell={NumberCell} />
              <Column field="numOfCleanRecords" title="Clean" cell={NumberCell} />
              <Column field="numOfBlockedRecords" title="Blocked" cell={NumberCell} />
              <Column field="numOfDNCRecords" title="DNC" cell={NumberCell} />
              <Column field="numOfWirelessRecords" title="Wireless" cell={NumberCell} />
              <Column field="numOfVoIPRecords" title="VoIP" cell={NumberCell} />
              <Column field="numOfEBRRecords" title="EBR" cell={NumberCell} />
              <Column field="numOfInvalidRecords" title="Invalid" cell={NumberCell} />
            </Grid>
            {isApi && isLoadingRecords && (
              <div className="text-center py-3">
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Loading scrub records...
              </div>
            )}
            {isApi && !isLoadingRecords && phoneRecordsTotal > 0 && (
              <div className="mt-3">
                <h6>Scrub Records ({phoneRecordsTotal.toLocaleString()})</h6>
                <Grid
                  data={phoneRecords.map((r, i) => ({
                    ...r,
                    rowNum: recordsPage.skip + i + 1,
                  }))}
                  total={phoneRecordsTotal}
                  pageable={{ buttonCount: 5, pageSizes: [25, 50, 100] }}
                  skip={recordsPage.skip}
                  take={recordsPage.take}
                  onPageChange={(e: GridPageChangeEvent) =>
                    setRecordsPage({ skip: e.page.skip, take: e.page.take })
                  }
                  style={{ maxHeight: "350px" }}
                >
                  <Column field="rowNum" title="#" width="80px" />
                  <Column field="phoneNumber" title="Phone Number" cell={(props) => (
                    <td>{formatPhoneNumber(props.dataItem.phoneNumber)}</td>
                  )} />
                  <Column field="status" title="Status" />
                </Grid>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScrubSessionDetail;
